// Organization-wide ingestion health, read once for every surface that shows
// it (agion/Agion#2091).
//
// Two shells signal this fault in their own way: the current shell puts a
// banner above the page, the next shell puts a dot in the rail, following the
// same design-of-record decision that gave the budget alert a dot there. Both
// read through this hook and through one React Query key, so the two can
// never disagree, and a member who has both surfaces open still causes one
// fan-out across the organization's hosts rather than two.
import { useQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { queryPolicies } from '@/shared/api/query-policies'
import { ingestionHealthService } from '@/services/ingestion-health.service'
import type { IngestionHealth, IngestionHealthSource } from '@/types/ingestion-health.types'

export const orgIngestionHealthQueryKey = ['intelligence', 'ingestion-health'] as const

/** How often the answer is refreshed. A stopped source is not an emergency. */
const REFETCH_INTERVAL_MS = 5 * 60 * 1000

export function useOrgIngestionHealth(enabled: boolean) {
  return useQuery<IngestionHealth>({
    ...queryPolicies.catalog,
    enabled,
    queryKey: orgIngestionHealthQueryKey,
    queryFn: () => ingestionHealthService.getOrgIngestionHealth(),
    refetchInterval: REFETCH_INTERVAL_MS,
    // A read that failed says nothing on either surface. Retrying a fan-out
    // that just timed out only costs the hosts another sweep.
    retry: false,
  })
}

/** The sources that have stopped collecting data. */
export function stoppedSources(health: IngestionHealth): IngestionHealthSource[] {
  return health.sources.filter((s) => s.severity === 'attention')
}

/** The sources still collecting data that reported something worth a look. */
export function warningSources(health: IngestionHealth): IngestionHealthSource[] {
  return health.sources.filter((s) => s.severity === 'warning')
}

/** Presentation of the server's delayed surfaces, without calculating age. */
export function derivedVisibilityDetail(source: IngestionHealthSource, t: TFunction<'pages'>): string {
  if (!source.delayedSurfaces?.length) return ''
  return t('intelligence.ingestionHealth.derivedUpdatesDelayed', {
    name: source.displayName || source.capsuleName || source.capsuleId,
    surfaces: source.delayedSurfaces.map(({ surface }) =>
      t(`intelligence.ingestionHealth.surface_${surface}`),
    ).join(', '),
    minutes: source.warningAfterMinutes,
  })
}

/**
 * The one word both shells signal with.
 *
 * A stopped source is critical: data an administrator believes is arriving is
 * not arriving. A non-terminal signal, or a host whose health could not be
 * read at all, is a warning; silence about an unreadable host is the defect
 * this package exists to remove, so it cannot resolve to nothing. A failure
 * the worker is still retrying on its own budget resolves to nothing, because
 * it is not yet anybody's problem.
 */
export function ingestionHealthLevel(
  health: IngestionHealth | undefined,
): 'critical' | 'warning' | null {
  if (!health) return null
  if (stoppedSources(health).length > 0) return 'critical'
  if (warningSources(health).length > 0 || health.unavailableHosts.length > 0) return 'warning'
  return null
}
